import type {
  SdkApiHandle,
  SdkNotificationHistory,
  SdkNotificationHistoryDetail,
  SdkNotificationHistoryKind,
  SdkNotificationHistoryOptions
} from '@Pages/pageRuntime/sdk/types';

import { useApi } from './useApi';

const URL = '/api/notification-history';
export const NOTIFICATION_HISTORY_QUERY_KEY = 'notification-history';

type QueryParams = Record<string, string | number>;

/**
 * Indexed `records[0][id]` keys: Axios's default `records[][id]` makes Laravel read
 * each nested key as its own entry. Unset filters stay out of the request.
 */
export function notificationHistoryParams(options: SdkNotificationHistoryOptions): QueryParams {
  const params: QueryParams = {
    status: options.status ?? 'upcoming',
    page: options.page ?? 1,
    per_page: options.perPage ?? 25
  };
  (options.records ?? []).forEach((record, index) => {
    params[`records[${index}][id]`] = record.recordId;
    params[`records[${index}][resource_id]`] = record.resourceId;
  });
  const filters: Record<string, string | null | undefined> = {
    resource_id: options.resourceId,
    search: options.search,
    type_name: options.typeName,
    template_name: options.templateName,
    source: options.source,
    email_status: options.emailStatus,
    date_from: options.dateFrom,
    date_before: options.dateBefore,
    timezone: options.timezone,
    sort: options.sort
  };
  for (const [key, value] of Object.entries(filters)) {
    if (value !== null && value !== undefined && value !== '') {
      params[key] = value;
    }
  }
  return params;
}

export function useNotificationHistory(
  options: SdkNotificationHistoryOptions
): SdkApiHandle<SdkNotificationHistory> {
  const params = notificationHistoryParams(options);
  const hasScope = (options.records?.length ?? 0) > 0 || options.workspace === true;
  return useApi(
    [NOTIFICATION_HISTORY_QUERY_KEY, 'list', params],
    (api) => api.get<SdkNotificationHistory>(URL, { params }),
    { enabled: (options.enabled ?? true) && hasScope, keepPreviousData: true }
  );
}

export function useNotificationHistoryDetail(
  kind: SdkNotificationHistoryKind | null | undefined,
  notificationId: string | null | undefined
): SdkApiHandle<SdkNotificationHistoryDetail> {
  return useApi(
    [NOTIFICATION_HISTORY_QUERY_KEY, 'detail', kind, notificationId],
    (api) =>
      api.get<SdkNotificationHistoryDetail>(
        `${URL}/${encodeURIComponent(kind ?? '')}/${encodeURIComponent(notificationId ?? '')}`
      ),
    { enabled: !!kind && !!notificationId }
  );
}
